import { type ReactNode, useCallback, useRef, useState } from 'react';

interface Props {
  onRefresh: () => Promise<void>;
  children: ReactNode;
}

const THRESHOLD = 60;
const MAX_PULL = 100;
const REFRESHING_HEIGHT = 40;

type Status = 'idle' | 'refreshing' | 'done';

export default function PullToRefresh({ onRefresh, children }: Props) {
  const [pullY, setPullY] = useState(0);
  const [status, setStatus] = useState<Status>('idle');
  const startY = useRef(0);
  const pulling = useRef(false);

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (status === 'refreshing') return;
      if (status === 'done') {
        setStatus('idle');
        setPullY(0);
      }
      if (window.scrollY === 0) {
        startY.current = e.touches[0].clientY;
        pulling.current = true;
      }
    },
    [status],
  );

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!pulling.current) return;
    const delta = e.touches[0].clientY - startY.current;
    if (delta > 0) {
      setPullY(Math.min(delta * 0.5, MAX_PULL));
    }
  }, []);

  const onTouchEnd = useCallback(async () => {
    if (!pulling.current) return;
    pulling.current = false;
    if (pullY >= THRESHOLD) {
      setStatus('refreshing');
      setPullY(REFRESHING_HEIGHT);
      try {
        await onRefresh();
      } finally {
        setStatus('done');
        setTimeout(() => {
          setStatus('idle');
          setPullY(0);
        }, 1500);
      }
    } else {
      setPullY(0);
    }
  }, [pullY, onRefresh]);

  const pastThreshold = pullY >= THRESHOLD;
  const showFixed = status === 'refreshing' || status === 'done';
  const indicatorHeight = showFixed ? REFRESHING_HEIGHT : pullY;

  return (
    <div
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div
        className="ptr-indicator"
        style={{
          height: indicatorHeight,
          opacity: indicatorHeight > 10 ? 1 : 0,
        }}
      >
        <span
          className={`ptr-arrow ${pastThreshold || showFixed ? 'ptr-ready' : ''}`}
        >
          {status === 'refreshing'
            ? 'Refreshing\u2026'
            : status === 'done'
              ? 'Refreshed'
              : pastThreshold
                ? '\u2191 Release'
                : '\u2193 Pull to refresh'}
        </span>
      </div>
      {children}
    </div>
  );
}
